import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
} from "@mui/material";
import DirtyFormSpy from "components/DirtyFormSpy";
import Field from "components/Field";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { pick } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfBaseFilter,
  ListResponseOfSkuLearningLevel,
  SkuLearningLevel,
  SkuLearningLevelRequest,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { SkuFormProps, SkuLearningLevelWithId } from "types/sku-related-types";
import translateError from "utils/translateError";
import SelectLearningLevel from "./SelectLearningLevel";

type SkuLearningLevelFormValues = {
  skuId: string;
  learningLevelId: string;
  skuLearningLevelRequest: SkuLearningLevelRequest;
};

const { Form } = withTypes<SkuLearningLevelFormValues>();

const SkuLearningLevels = ({
  sku,
  onDirty,
  onSkuUpdated,
  onClean,
}: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuLearningLevel | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const DeleteButton = ({ value }: { value: SkuLearningLevel }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setDeleting(value)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(skuService.deleteSkuLearningLevel(id));
        queryClient.invalidateQueries(["skuLearningLevels", sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuLearningLevelDeleted" }),
            Severity.Warning
          )
        );
      } catch (err: any) {
        console.log(err);
      }
    }
  }, [deleting?.id, queryClient, registerPromise, sku.id, skuService, t]);

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuLearningLevel>(
    ["skuLearningLevels", sku.id!],
    () =>
      skuService.getSkuLearningLevels(
        sku.id!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuLearningLevels = useMemo(
    () => (data?.data ?? []) as SkuLearningLevelWithId[],
    [data?.data]
  );

  const init = useMemo<SkuLearningLevelFormValues>(
    () => ({
      skuId: pick(sku.id! ?? "", ["id"]),
      skuLearningLevelRequest: new SkuLearningLevelRequest(),
      learningLevelId: "",
    }),
    [sku]
  );

  const form = useMemo(() => {
    const cells: Array<TableCellType> = [
      {
        accessor: "learningLevel.value",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuLearningLevelLabel" }),
      },
      {
        accessor: "value",
        align: "center",
        width: 1,
        type: "node",
        View: DeleteButton,
        label: "",
        noSort: true,
      },
    ];

    const onSubmit = async (values: SkuLearningLevelFormValues) => {
      const existingLearningLevel = skuLearningLevels.find(
        (x) => x.learningLevel?.id! === values.learningLevelId!
      );

      if (existingLearningLevel !== undefined) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuLearningLevelRepeated" }),
            Severity.Warning
          )
        );
        onSkuUpdated && onSkuUpdated();
        return;
      }

      try {
        const result = await registerPromise(
          skuService.saveSkuLearningLevel(
            sku.id!,
            SkuLearningLevelRequest.fromJS(values)
          )
        );
        queryClient.invalidateQueries(["skuLearningLevels", sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "skuLearningLevelCreated" }))
        );
        onSkuUpdated && onSkuUpdated(sku.id!);
        return result;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, dirty, values }) => {
          return (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <DirtyFormSpy onDirty={onDirty} onClean={onClean} />

              <Box mb={3}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Field name="learningLevelId">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectLearningLevel
                            label={t({
                              id: "selectLearningLevelLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            onDirty={onDirty}
                            onClean={onClean}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Box textAlign="right">
                      <Button
                        variant="outlined"
                        type="submit"
                        color="primary"
                        disabled={
                          submitting || !dirty || !values.learningLevelId
                        }
                        data-cy="addLearningLevelButton"
                      >
                        <T id="skuLearningLevelCreateButtonLabel" />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <SortableTable
                {...{
                  rows: skuLearningLevels,
                  loading: isFetching,
                  dense: true,
                  cells,
                }}
              />

              <Dialog open={!!deleting} disableEnforceFocus>
                <DialogTitle>
                  <T id="skuLearningLevelDeleteTitle" />
                </DialogTitle>
                <DialogContent dividers>
                  <T id="skuLearningLevelDeleteQuestion" />
                </DialogContent>
                <DialogActions>
                  <Button
                    autoFocus
                    onClick={() => setDeleting(null)}
                    color="primary"
                    data-cy="cancelButton"
                  >
                    <T id="cancelButton" />
                  </Button>
                  <Button
                    onClick={handleDelete}
                    color="secondary"
                    variant="contained"
                    data-cy="deleteButton"
                  >
                    <T id="deleteButton" />
                  </Button>
                </DialogActions>
              </Dialog>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    );
  }, [
    init,
    skuLearningLevels,
    registerPromise,
    skuService,
    sku.id,
    t,
    onSkuUpdated,
    queryClient,
    onDirty,
    onClean,
    isFetching,
    deleting,
    handleDelete,
    snackbarMessage,
  ]);

  return form;
};

export default withErrorBoundary(SkuLearningLevels);
